import { useEffect, useMemo, useState } from 'react';
import AppShell from '../app/AppShell';
import {
  FeedbackMessage,
  PaginationFooter,
  SaveConfirmationModal,
  ToastStack,
} from '../components/common';
import {
  EntityEditModal,
  EntityFilters,
  EntityForm,
  EntityTable,
  EntityToolbar,
} from '../components/entity';
import { supplierFormInitialValues, supplierStatuses } from '../data/supplierConstants';
import useCancelConfirmation from '../hooks/useCancelConfirmation';
import useSaveConfirmation from '../hooks/useSaveConfirmation';
import useSuppliers from '../hooks/useSuppliers';
import { hasChangedFormValues } from '../utils/formDirty';

const SUPPLIER_PAGE_SIZE = 5;

function SupplierPage({
  navigationItems,
  activePath,
  activeSearch = '',
  onNavigate,
}) {
  const saveConfirmation = useSaveConfirmation();
  const cancelConfirmation = useCancelConfirmation();
  const [currentPage, setCurrentPage] = useState(1);
  const {
    activeView,
    filterValues,
    formValues,
    formErrors,
    filteredSuppliers,
    isLoading,
    isSaving,
    loadError,
    submitError,
    successMessage,
    editingSupplier,
    editValues,
    editErrors,
    editError,
    isEditSaving,
    setActiveView,
    clearFormPhoto,
    handleFormChange,
    handleFilterChange,
    handleFilterSubmit,
    handleFilterReset,
    handleSubmit,
    handleCancel,
    openEditModal,
    closeEditModal,
    clearEditPhoto,
    handleEditChange,
    handleEditSubmit,
  } = useSuppliers();
  const toasts = [
    loadError ? { id: 'supplier-load-error', tone: 'error', message: loadError } : null,
    submitError ? { id: 'supplier-submit-error', tone: 'error', message: submitError } : null,
    successMessage ? { id: 'supplier-success', tone: 'success', message: successMessage } : null,
    editError ? { id: 'supplier-edit-error', tone: 'error', message: editError } : null,
  ].filter(Boolean);
  const totalPages = Math.max(1, Math.ceil(filteredSuppliers.length / SUPPLIER_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * SUPPLIER_PAGE_SIZE;
  const endIndex = Math.min(startIndex + SUPPLIER_PAGE_SIZE, filteredSuppliers.length);
  const paginatedSuppliers = useMemo(
    () => filteredSuppliers.slice(startIndex, endIndex),
    [endIndex, filteredSuppliers, startIndex]
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(activeSearch);

    if (searchParams.get('view') === 'create') {
      setActiveView('create');
    }
  }, [activeSearch, setActiveView]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeView, filterValues.supplierName, filterValues.status]);

  return (
    <AppShell
      navigationItems={navigationItems}
      activePath={activePath}
      onNavigate={onNavigate}
    >
      <ToastStack toasts={toasts} />
      <section className="mt-5 px-4 sm:mt-6 sm:px-6 lg:px-[26px]">
        <div className="grid gap-5">
          <EntityToolbar activeView={activeView} onChangeView={setActiveView} />

          {activeView === 'list' ? (
            <>
              <EntityFilters
                values={filterValues}
                onChange={handleFilterChange}
                onSubmit={handleFilterSubmit}
                onReset={handleFilterReset}
                statusOptions={supplierStatuses}
                showDateFilter={false}
                showApply={false}
              />

              <div className="grid gap-3">
                <div>
                  <h2 className="m-0 text-[1.25rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.4rem]">
                    Supplier List
                  </h2>
                  <p className="m-0 mt-2 text-[0.96rem] text-[var(--theme-text-muted)]">
                    Manage supplier contact records and account status.
                  </p>
                </div>

                {isLoading ? (
                  <FeedbackMessage tone="info" message="Loading supplier records..." />
                ) : (
                  <div className="grid gap-3">
                    <EntityTable records={paginatedSuppliers} onEditRecord={openEditModal} />
                    <div className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)]">
                      <PaginationFooter
                        currentPage={safeCurrentPage}
                        endItem={filteredSuppliers.length ? endIndex : 0}
                        isNextDisabled={safeCurrentPage >= totalPages}
                        isPreviousDisabled={safeCurrentPage <= 1}
                        onNext={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                        onPrevious={() => setCurrentPage((page) => Math.max(1, page - 1))}
                        startItem={filteredSuppliers.length ? startIndex + 1 : 0}
                        totalItems={filteredSuppliers.length}
                        totalPages={totalPages}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <EntityForm
                values={formValues}
                errors={formErrors}
                isSaving={isSaving}
                onChange={handleFormChange}
                onClearPhoto={clearFormPhoto}
                onSubmit={(event) =>
                  saveConfirmation.requestConfirmation(event, handleSubmit, {
                    title: 'Save supplier profile?',
                    message: 'Please confirm the supplier details before saving.',
                    confirmLabel: 'Save Supplier',
                  })
                }
                onCancel={(event) =>
                  cancelConfirmation.requestCancel(event, {
                    shouldConfirm: () =>
                      hasChangedFormValues(formValues, supplierFormInitialValues),
                    onCancel: handleCancel,
                    title: 'Cancel supplier profile?',
                    message: 'You have unsaved supplier inputs. Discard them?',
                  })
                }
              />
            </>
          )}
        </div>
      </section>

      {editingSupplier ? (
        <EntityEditModal
          values={editValues}
          errors={editErrors}
          statusOptions={supplierStatuses}
          isSaving={isEditSaving}
          onChange={handleEditChange}
          onClearPhoto={clearEditPhoto}
          onClose={(event) =>
            cancelConfirmation.requestCancel(event, {
              shouldConfirm: () =>
                editingSupplier
                  ? hasChangedFormValues(editValues, getSupplierEditBaseline(editingSupplier))
                  : false,
              onCancel: closeEditModal,
              title: 'Cancel editing?',
              message: 'You have unsaved supplier changes. Discard them?',
            })
          }
          onSubmit={(event) =>
            saveConfirmation.requestConfirmation(event, handleEditSubmit, {
              title: 'Save supplier changes?',
              message: 'Please confirm the updated supplier details before saving.',
              confirmLabel: 'Save Changes',
            })
          }
        />
      ) : null}

      <SaveConfirmationModal {...saveConfirmation.modalProps} />
      <SaveConfirmationModal {...cancelConfirmation.modalProps} />
    </AppShell>
  );
}

function getSupplierEditBaseline(supplier) {
  return {
    companyName: supplier.companyName,
    sacksMark: supplier.sacksMark || '',
    contactNumber: supplier.contactNumber || '',
    address: supplier.address,
    photoUrl: supplier.photoUrl || '',
    status: supplier.status,
  };
}

export default SupplierPage;
